import * as React from 'react'

import { Layer, Source, useMap } from 'react-map-gl/mapbox'

import {
  bareLabelLiftPx,
  chipCarryingLabelDeclutterLayout,
  hiddenWhen,
  IS_SELECTED,
  labelCasingPaint,
  labelHaloPaint,
  labelMeasureFont,
  labelTypographyLayout,
  LABEL_MARKER_GAP_PX,
  pinLabelInkPaint,
  PIN_LABEL,
  SELECTED_LABEL_PLACEMENT,
} from '~/client/src/shared/map/labelLayerStyle'
import {
  capLabelLines,
  ILabelParts,
} from '~/client/src/shared/map/labelLineCap'
import {
  minPlatePadYPx,
  WORKFLOW_LABEL_PLATE,
} from '~/client/src/shared/map/labelPlateSpec'
import {
  bandedImage,
  bandedImageBands,
  fittedImageSize,
  rampedOneLevelAhead,
} from '~/client/src/shared/map/mapFittedImage'
import {
  getZoomScale,
  imageBandSuffix,
} from '~/client/src/shared/map/mapZoomRule'
import { MARKER_CLEARANCE_PX } from '~/client/src/shared/map/markerGeometry'

import { IGroundContext } from '../MapBoxViewer/labelGroundTone'
import { IPlateDecision } from '../MapBoxViewer/labelPlateDecision'
import {
  measureLabelTextBox,
  useLabelFontsReady,
} from '../MapBoxViewer/labelTextBox'
import {
  WORKFLOW_PIN_LABELS_ANCHORED_LAYER,
  WORKFLOW_PIN_LABELS_LAYER,
  WORKFLOW_PIN_LABELS_SELECTED_ANCHORED_LAYER,
  WORKFLOW_PIN_LABELS_SELECTED_LAYER,
} from '../MapBoxViewer/mapboxConstants'
import {
  MAX_ITEM_ZOOM_LEVEL,
  PROJECT_MARKER_ZOOM_LEVEL,
} from '../MapBoxViewer/MapBoxViewer.store'
import { ICON_TEXT_OCCLUSION_PAINT } from '../MapBoxViewer/mapOcclusionRule'
import { useCulledPinKeys } from '../MapBoxViewer/useCulledPinKeys'
import {
  ILabelAnchor,
  IRenderedLabels,
  LabelBoxAt,
  useLabelGroundTones,
} from '../MapBoxViewer/useLabelGroundTones'
import { useReleaseWithoutFade } from '../MapBoxViewer/useReleaseWithoutFade'
import {
  CHIP_LABEL_FIT,
  PLATE_SUFFIX,
  registerCompanyLogoUrls,
  registerMissingCompanyLogoImage,
} from '../SitemapHelpers/utils/GLCompanyLogoImageRegistrar'
import {
  IPlanFootprint,
  isOnPlanSheet,
} from '../SitemapHelpers/utils/planFootprints'

// One GL symbol label per workflow pin (STX1-686 D5); complements the per-markup
// code labels for pin-only workflows that have no markup to carry a label.
export const WORKFLOW_PIN_LABELS_LAYER_ID = WORKFLOW_PIN_LABELS_LAYER
// Anchored labels place dynamically, and that is a layer-wide setting — so they get their own
// layer off the same source. It sits BELOW the free-standing one: mapbox places the topmost
// layer first, so a label with three candidate positions is the one that yields to a label
// that only has its own spot.
export const WORKFLOW_PIN_LABELS_ANCHORED_LAYER_ID =
  WORKFLOW_PIN_LABELS_ANCHORED_LAYER

export const WORKFLOW_PIN_LABEL_LAYER_IDS = [
  WORKFLOW_PIN_LABELS_LAYER_ID,
  WORKFLOW_PIN_LABELS_ANCHORED_LAYER_ID,
  WORKFLOW_PIN_LABELS_SELECTED_LAYER,
  WORKFLOW_PIN_LABELS_SELECTED_ANCHORED_LAYER,
]

export interface IWorkflowPinLabelPoint {
  id: string
  // The pin group this label names. A tap on the label resolves through the host's
  // workflowsByCoordKey exactly as a tap on the pin does, and the hover probe matches
  // label to pin by it, so the two highlight together.
  coordKey: string
  latitude: number
  longitude: number
  // Worded by the host, capped here: only the title is ever shortened.
  labelParts: ILabelParts
  // Style-image id of the owning company's logo chip, or the default chip when it has no logo.
  logoImageId: string
  // Lower = placed first, so it survives collision when labels declutter.
  sortKey?: number
  // Rides a location marker, so it has no coordinate of its own and the marker's circle and
  // name label already stand where this label would go. Routes it onto the dynamic layer.
  anchored?: boolean
  // Selected workflow: the label switches to brand-blue ink on the light casing.
  isSelected?: boolean
}

interface IProps {
  points: IWorkflowPinLabelPoint[]
  // imageId → logo URL, fed to the registrar so the styleimagemissing handler
  // can rasterize chips lazily (and again after style reloads).
  logoUrls?: Map<string, string>
  // The "Show workflow pin labels" setting. Off still labels the selected pins: the toggle
  // declutters the map, it does not hide which workflow is active (STX1-990).
  showUnselected?: boolean
  // The pin layers these labels name. A label whose pin lost placement is dropped, not left
  // standing alone.
  pinLayerIds?: string[]
  // Honors the "Allow labels to overlap" Map Setting (like CollisionLayer): off
  // lets Mapbox declutter colliding labels instead of piling them at low zoom.
  allowOverlap?: boolean
  // coordKey under the pointer, from the pin layer's hover probe — the label this
  // names wears the same blue as the pin it belongs to.
  hoveredCoordKey?: string
  // What the map is made of, so each label can resolve the ground under its glyphs.
  ground: IGroundContext
  // Where plan sheets are. A label standing on one is plated: a casing cannot separate ink
  // from a page that already holds both white paper and black linework.
  planFootprints?: IPlanFootprint[]
  // The map view's "scale with zoom" setting. The pin itself already rides this ramp; its label
  // does too now, so the two stop disagreeing as the camera pulls back.
  isZoomScalingEnabled?: boolean
  // The host draws the labels itself (the export's Optimized labels): the layer draws none but
  // still reads each label's ground, and hands it out through onLabelTones.
  isLabelHidden?: boolean
  onLabelTones?: (tones: Map<string, IPlateDecision>) => void
  // Changes when the ground under the labels changes with neither the camera nor a source saying
  // so (an export hiding a map file), so every label is read again.
  rerunKey?: string
}

const LABEL_TEXT_SIZE = PIN_LABEL.sizePx
const LABEL_LINE_HEIGHT_PX = PIN_LABEL.lineHeightPx
// Per line count, not from the shortest label: the plate paints its whole fitted box, so
// padding it past the clamp floor would be visible. Only a one-line label falls short.
const PLATE_PAD_Y_BY_LINES = [1, 2, 3].map(lines =>
  minPlatePadYPx(WORKFLOW_LABEL_PLATE, lines * PIN_LABEL.lineHeightPx),
)
const platePadding = (lines: number): Array<number> => {
  const padY =
    PLATE_PAD_Y_BY_LINES[Math.min(lines, PLATE_PAD_Y_BY_LINES.length) - 1]
  return [padY, WORKFLOW_LABEL_PLATE.padXPx, padY, 0]
}

// The same case, times a factor, so it can ride the ramp one level ahead of the tile. Padding is
// fixed pixels in the image's own space; left unramped it grows against the glyphs at every step
// down the ramp.
const fitPaddingAt = (factor: number): unknown => {
  const times = (pad: Array<number>): unknown => [
    'literal',
    pad.map(px => px * factor),
  ]
  return [
    'case',
    ['!', ['get', 'plated']],
    times([CHIP_FIT_PAD_PX, 0, CHIP_FIT_PAD_PX, 0]),
    ['==', ['get', 'lines'], 1],
    times(platePadding(1)),
    ['==', ['get', 'lines'], 2],
    times(platePadding(2)),
    times(platePadding(3)),
  ]
}

const RENDERED_PIN_LABELS: IRenderedLabels = {
  layerId: WORKFLOW_PIN_LABEL_LAYER_IDS,
  idProperty: 'labelId',
}

const LABEL_MAX_WIDTH_PX = 216
// Canvas advances and mapbox's own shaping agree only to a fraction of a percent, and the line
// count is a cliff: one line needs 5px of plate padding to clear the perspective clamp, two need
// none. A label measuring 1.4px over this column was laid out by mapbox on one line and got a
// plate padded for a second, so its text sat high. Erring the other way only pads more than the
// clamp needs, so the column is widened past what the two can disagree by. The 3% is empirical.
const WRAP_AGREEMENT_MARGIN_PX = LABEL_MAX_WIDTH_PX * 0.03
// The line cap errs the other way, narrowed by the same margin: a line past the cap breaks it,
// while a plate padded for one line too many only pads more than it needs.
const LABEL_CAP_WIDTH_PX = LABEL_MAX_WIDTH_PX - WRAP_AGREEMENT_MARGIN_PX
const LABEL_MAX_LINES = 3
const LABELS_SOURCE_ID = 'workflow-pin-labels-source'
const LABEL_MEASURE_FONT = labelMeasureFont(PIN_LABEL)
const LABEL_MAX_WIDTH_EM = LABEL_MAX_WIDTH_PX / LABEL_TEXT_SIZE
// Where an unplated label's line box sits below the pin tip, so its CASING clears the tip by
// LABEL_MARKER_GAP_PX — the same gap, measured the same way, as a location label clearing its
// circle. It was a stated 0.6em, which put the ink 9.1px below the tip while a location label
// got 1.5px from its circle.
const LABEL_TOP_OFFSET_EM =
  bareLabelLiftPx(PIN_LABEL, LABEL_TEXT_SIZE, PIN_LABEL.haloWidthPx) /
  LABEL_TEXT_SIZE

// A plated label's edge is its plate, not its casing, and the plate extends above the text by
// the fit padding plus the corner row. Without this the plate's top landed ON the pin tip: the
// unplated offset happens to be almost exactly what the plate hangs upward, so the two
// cancelled and the gap came out at zero.
const platedTopOffsetEm = (lines: number): number =>
  (LABEL_MARKER_GAP_PX +
    PLATE_PAD_Y_BY_LINES[Math.min(lines, PLATE_PAD_Y_BY_LINES.length) - 1] +
    WORKFLOW_LABEL_PLATE.radiusPx) /
  LABEL_TEXT_SIZE
// icon-text-fit grows the chip image around the shaped text, so the chip hangs to the LEFT
// of it. Nudging the text right by half that lead re-centers chip+text under the pin — a
// constant, because the lead is fixed, so nothing has to measure the label.
const CHIP_HALF_LEAD_EM = CHIP_LABEL_FIT.leadPx / 2 / LABEL_TEXT_SIZE

// fitPaddingAt's branches, in its order: a plate's padding and its offset have to agree.
// Selected here, not sent — a vector tile property cannot hold an array.
// The properties both expressions read, built in one place so the producer cannot drift from
// them silently.
export const pinLabelExpressionProperties = (
  logoImageId: string,
  plated: boolean,
  lines: number,
): { logo: string; plated: boolean; lines: number } => ({
  logo: logoImageId,
  plated,
  lines,
})

export const pinLabelImageAt = (band: number | null): unknown => [
  'case',
  ['!', ['get', 'plated']],
  ['concat', ['get', 'logo'], imageBandSuffix(band)],
  ['concat', ['get', 'logo'], imageBandSuffix(band), PLATE_SUFFIX],
]

export const textOffsetAt = (): unknown => {
  const at = (x: number, y: number): unknown => ['literal', [x, y]]
  return [
    'case',
    ['!', ['get', 'plated']],
    at(CHIP_HALF_LEAD_EM, LABEL_TOP_OFFSET_EM),
    ['==', ['get', 'lines'], 1],
    at(CHIP_HALF_LEAD_EM, platedTopOffsetEm(1)),
    ['==', ['get', 'lines'], 2],
    at(CHIP_HALF_LEAD_EM, platedTopOffsetEm(2)),
    at(CHIP_HALF_LEAD_EM, platedTopOffsetEm(3)),
  ]
}

// Pads the fit box past mapbox's perspective clamp, which is what stops the text riding high
// in the chip on a pitched map — derivation at CHIP_LABEL_FIT. Free visually: the padding is
// transparent stretch band. Symmetric, because a centred chip requires it; an icon-offset
// correcting an asymmetric box cancels out exactly.
export const CHIP_FIT_PAD_PX = Math.max(
  0,
  (CHIP_LABEL_FIT.minFitHeightPx - LABEL_LINE_HEIGHT_PX) / 2,
)

// Scale-free px from the pin's coordinate down to the middle of the label's first line, for the
// ground read. Plating-free on purpose: the plate is decided FROM this answer, so taking the
// plate into account here would close the loop. One value, because a label only ever hugs its
// own pin now — an anchored pin carries no label at all.
const PIN_LABEL_DROP_PX =
  LABEL_TOP_OFFSET_EM * LABEL_TEXT_SIZE + LABEL_LINE_HEIGHT_PX / 2

// The layers split one source. Filters rather than two sources: same-source symbols share
// a collision group, so the halves still declutter against each other. A selected label also
// paints from a twin in the selected tier, its resting copy hidden by RESTING_HIDDEN.
const ANCHORED_FILTER = ['get', 'anchored']
const FREE_STANDING_FILTER = ['!', ['get', 'anchored']]
export const PIN_LABEL_FILTERS = {
  anchored: ANCHORED_FILTER,
  freeStanding: FREE_STANDING_FILTER,
  selectedAnchored: ['all', IS_SELECTED, ANCHORED_FILTER],
  selectedFreeStanding: ['all', IS_SELECTED, FREE_STANDING_FILTER],
}
export const RESTING_HIDDEN = hiddenWhen(IS_SELECTED)

// One side, always: an anchored label sits to the RIGHT of its pin, or it does not place.
// 'left' is mapbox's name for the anchor — the edge of the text that meets the point.
//
// Deliberately not a candidate list: placement takes the far flank whenever the near one is
// contested, so the same workflow drew on either side depending on its neighbourhood.
const ANCHORED_TEXT_ANCHOR = 'left'

// One distance, applied in whichever direction placement picks: clear the host marker's
// COLLISION box, not just its painted circle. The label always yields to its marker, so a radial
// that only clears the circle let a closures badge or the live ring cull the label it hosts.
// A variable anchor accepts only this scalar — there is no x/y form — and "clear the marker" is
// the same requirement on both flanks.
const ANCHORED_RADIAL_EM = MARKER_CLEARANCE_PX / LABEL_TEXT_SIZE

// The radial has to clear the label's LEADING edge, which is the chip, not the text — the chip
// hangs a full lead left of the text box the anchor positions. Without this it
// overhangs back into the marker the label just stepped around.
const CHIP_LEAD_EM = CHIP_LABEL_FIT.leadPx / LABEL_TEXT_SIZE

// A plate hangs above its text, so a plated label needs the radial pushed out by what the
// plate overhangs. Errs the safe way sideways too, where the overhang is horizontal: a little
// more daylight, never less.
const platedRadialExtraEm = (lines: number): number =>
  platedTopOffsetEm(lines) - LABEL_MARKER_GAP_PX / LABEL_TEXT_SIZE

export const labelPointsToShow = (
  points: IWorkflowPinLabelPoint[],
  showUnselected: boolean,
): IWorkflowPinLabelPoint[] =>
  showUnselected ? points : points.filter(p => p.isSelected)

const cappedLabel = (
  cache: Map<string, string>,
  parts: ILabelParts,
): string => {
  const key = [parts.prefix, parts.separator, parts.title, parts.suffix].join(
    '\u0000',
  )
  let label = cache.get(key)
  if (label === undefined) {
    label = capLabelLines(
      parts,
      text =>
        measureLabelTextBox(
          text,
          LABEL_MEASURE_FONT,
          LABEL_CAP_WIDTH_PX,
          LABEL_TEXT_SIZE,
        ).lines,
      LABEL_MAX_LINES,
    )
    cache.set(key, label)
  }
  return label
}

const EMPTY_FC: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
}

const GLWorkflowPinLabelLayer: React.FC<IProps> = ({
  points,
  logoUrls,
  showUnselected = true,
  pinLayerIds,
  allowOverlap = true,
  hoveredCoordKey,
  ground,
  planFootprints,
  isZoomScalingEnabled,
  isLabelHidden = false,
  onLabelTones,
  rerunKey,
}) => {
  const { current: map } = useMap()
  // Which logo set the label tiles were last built against — see the re-layout below.
  const reLaidOutForRef = React.useRef<Map<string, string> | undefined>(
    undefined,
  )
  // Before the layer below it, not in an effect. GL fires styleimagemissing ONCE per id per
  // style, and the first firing happens while this component's own <Layer> lays out — an effect
  // runs after that, so a handler that reached an empty registry lost the image for the session.
  React.useMemo(() => {
    if (logoUrls?.size) registerCompanyLogoUrls(logoUrls)
  }, [logoUrls])

  const fontsReady = useLabelFontsReady()
  // A cap taken on the fallback face is retaken once the real one arrives.
  const capCache = React.useMemo(() => new Map<string, string>(), [fontsReady])
  // The one capped string every consumer reads: the feature's text, the plate's line count and
  // the ground read. Cached by its parts, because a selection change rebuilds every point
  // without changing what any of them says.
  const labelById = React.useMemo(
    () => new Map(points.map(p => [p.id, cappedLabel(capCache, p.labelParts)])),
    [points, capCache],
  )

  // One measurement, shared by the plate's padding and by the offset that keeps its painted gap
  // constant — they have to agree, or the plate is padded for one label and placed for another.
  // Cached inside measureLabelTextBox, so asking twice costs nothing.
  const linesOf = React.useCallback(
    (label: string): number =>
      measureLabelTextBox(
        label,
        LABEL_MEASURE_FONT,
        LABEL_MAX_WIDTH_PX + WRAP_AGREEMENT_MARGIN_PX,
        LABEL_TEXT_SIZE,
      ).lines,
    [fontsReady],
  )

  const candidatePoints = React.useMemo(
    () => labelPointsToShow(points, showUnselected),
    [points, showUnselected],
  )
  const captionAnchors = React.useMemo(
    () =>
      candidatePoints.map(p => ({
        key: p.coordKey,
        longitude: p.longitude,
        latitude: p.latitude,
      })),
    [candidatePoints],
  )
  const culledPinKeys = useCulledPinKeys(map, pinLayerIds, captionAnchors)
  const shownPoints = React.useMemo(
    () =>
      culledPinKeys.size
        ? candidatePoints.filter(p => !culledPinKeys.has(p.coordKey))
        : candidatePoints,
    [candidatePoints, culledPinKeys],
  )
  const hasLabels = shownPoints.length > 0
  // Every label, including ones that will end up plated: the reading is what DECIDES the plate
  // now, so excluding the plated ones would be circular. All of them rather than the shown ones,
  // so a selection change under the toggle reuses the reading; only placed labels are read.
  const anchors = React.useMemo<ILabelAnchor[]>(
    () =>
      points.map(p => ({
        id: p.id,
        longitude: p.longitude,
        latitude: p.latitude,
      })),
    [points],
  )
  const anchoredIds = React.useMemo(
    () => new Set(points.filter(p => p.anchored).map(p => p.id)),
    [points],
  )
  // The label's own text box, so its ground is read across the whole label. The horizontal
  // offset is the same half-lead the text carries to clear the chip — sampling at the anchor
  // instead would sit off to one side of the text it is deciding for.
  const boxAt = React.useCallback<LabelBoxAt>(
    (anchor, zoom) => {
      const measured = measureLabelTextBox(
        labelById.get(anchor.id) ?? '',
        LABEL_MEASURE_FONT,
        LABEL_MAX_WIDTH_PX,
        LABEL_TEXT_SIZE,
      )
      // Measured at the tier size, then scaled: the cells have to cover the glyphs as DRAWN, or
      // the read answers for a label bigger than the one on screen — and it is this read that
      // now decides the plate.
      const scale = isZoomScalingEnabled ? getZoomScale(zoom) : 1
      const lineHeight = LABEL_LINE_HEIGHT_PX * scale
      const height = measured.lines * lineHeight
      // Which side an anchored label took is decided inside mapbox's placement pass and is not
      // readable from here, so its ground is read centred on the pin rather than below it: the
      // neutral answer among three possible sides instead of a confident wrong one.
      if (anchoredIds.has(anchor.id)) {
        return { dx: 0, dy: 0, width: measured.width * scale, height }
      }
      return {
        dx: CHIP_HALF_LEAD_EM * LABEL_TEXT_SIZE * scale,
        // Scaled like dx above and like the location layer's own mirror: the drop is scale-free
        // px, and this box has to cover the glyphs as DRAWN.
        dy: PIN_LABEL_DROP_PX * scale + (height - lineHeight) / 2,
        width: measured.width * scale,
        height,
      }
    },
    [labelById, anchoredIds, isZoomScalingEnabled],
  )
  // Selecting a label can show it without moving the camera, and only a placed label is read.
  const selectedKey = React.useMemo(
    () =>
      points
        .filter(p => p.isSelected)
        .map(p => p.id)
        .join('|'),
    [points],
  )
  const tones = useLabelGroundTones(
    anchors,
    ground,
    boxAt,
    RENDERED_PIN_LABELS,
    hasLabels,
    `${selectedKey}#${rerunKey ?? ''}`,
  )
  useReleaseWithoutFade(map, selectedKey)
  React.useEffect(() => onLabelTones?.(tones), [onLabelTones, tones])

  // Which labels need paper under them. Read from the ground the label actually stands on —
  // see decidePlate — rather than from whether a plan sheet happens to be present, which is what
  // lets the same drawing behave the same way whether it arrived as a plan or as a tileset.
  //
  // The footprint is still the fallback, for the window before a label's ground has been read:
  // a label on a sheet should never be briefly bare while the tiles are in flight.
  const platedIds = React.useMemo(() => {
    const ids = new Set<string>()
    points.forEach(p => {
      const read = tones.get(p.id)
      const plated =
        read?.plated ??
        (!!planFootprints?.length &&
          isOnPlanSheet(p.longitude, p.latitude, planFootprints))
      if (plated) ids.add(p.id)
    })
    return ids
  }, [points, tones, planFootprints])

  // Warming the plates costs 133KB of a banded logo's 197KB, and most projects never plate a
  // label. Chips are warmed unconditionally; plates only once something on the map is actually
  // wearing one, after which every band is warmed because the camera can reach any of them.
  // The gate cannot be the plan footprint: plating is decided by the GROUND now, so a label on
  // satellite plates too, and a footprint gate would starve exactly the case that needs it.
  const anyPlated = platedIds.size > 0
  // Two writes per hover change — set on the way in, clear on the way out — against a
  // collection that no longer changes at all. Guarded on the source rather than on
  // isStyleLoaded: the source is what setFeatureState addresses, and it can be absent while the
  // style reports loaded (a style reload remounts it). A feature whose tile is not built yet
  // still takes the state; mapbox applies it when the tile arrives.
  React.useEffect(() => {
    if (!hoveredCoordKey) return
    const gl = map?.getMap()
    if (!gl?.getSource(LABELS_SOURCE_ID)) return
    const feature = { source: LABELS_SOURCE_ID, id: hoveredCoordKey }
    gl.setFeatureState(feature, { hovered: true })
    return () => {
      // The source can be gone by teardown; the state went with it.
      if (!gl.getSource(LABELS_SOURCE_ID)) return
      gl.setFeatureState(feature, { hovered: false })
    }
  }, [map, hoveredCoordKey])

  // Both offsets per feature, in ems of the tier size, because the two layers ask for
  // different shapes: the fixed layer takes an x/y pair, the dynamic one a scalar radius.
  const offsets = React.useMemo(() => {
    const byId = new Map<string, { radialEm: number }>()
    points.forEach(p => {
      const plated = platedIds.has(p.id)
      const lines = plated ? linesOf(labelById.get(p.id) ?? '') : 1
      // No zoom band: text-offset is in ems and the ems already ride the label's own zoom
      // ramp, so this scales by itself. The old two-ended ramp applied that scaling a second
      // time, which is what closed the gap as you zoomed out.
      byId.set(p.id, {
        radialEm:
          ANCHORED_RADIAL_EM +
          CHIP_LEAD_EM +
          (plated ? platedRadialExtraEm(lines) : 0),
      })
    })
    return byId
  }, [points, platedIds, linesOf, labelById])

  // Which image each label wears, per zoom band. Rebuilt whole inside every band rather than
  // concatenating a band onto the choice, because zoom must be the outermost expression.
  const iconImage = React.useMemo(
    () => bandedImage(isZoomScalingEnabled, pinLabelImageAt),
    [isZoomScalingEnabled],
  )

  const data = React.useMemo<GeoJSON.FeatureCollection>(() => {
    if (!hasLabels) {
      return EMPTY_FC
    }
    return {
      type: 'FeatureCollection',
      features: shownPoints.map(p => {
        const ems = offsets.get(p.id)
        return {
          type: 'Feature' as const,
          properties: {
            label: labelById.get(p.id),
            ...pinLabelExpressionProperties(
              p.logoImageId,
              platedIds.has(p.id),
              // Only the plate reads this, and only to pick its padding. Measuring text to
              // POSITION a symbol is the mistake this repo already made; picking how much
              // plate to paint around it is not — see linesOf.
              platedIds.has(p.id) ? linesOf(labelById.get(p.id) ?? '') : 1,
            ),
            // Read only by the dynamic layer, which cannot take an x/y offset at all.
            radialEm: ems?.radialEm,
            anchored: !!p.anchored,
            sortKey: p.sortKey ?? 0,
            selected: !!p.isSelected,
            // `hovered` is deliberately absent: it lives in feature state now, so a pointer
            // sample no longer rebuilds and re-uploads all of these.
            coordKey: p.coordKey,
            // Carried so the ground reader can ask mapbox which labels it actually placed.
            labelId: p.id,
            tone: tones.get(p.id)?.tone ?? ground.basemap,
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [p.longitude, p.latitude],
          },
        }
      }),
    }
  }, [
    shownPoints,
    hasLabels,
    offsets,
    platedIds,
    tones,
    ground,
    linesOf,
    labelById,
  ])

  // Every band the icon-image expression can ask for, not just the one the camera is on.
  // styleimagemissing is a one-shot per id, so a band that first lays out while its raster is
  // still in flight never gets asked again and those labels keep their chip for the session —
  // which is exactly what a lazily warmed band looked like. The rasters are a few dozen px
  // square and the logo bitmap is decoded once for all of them, so warming the whole set costs
  // less than the race did. styleimagemissing stays as the style-reload fallback.
  React.useEffect(() => {
    if (!hasLabels || !logoUrls?.size) return
    const gl = map?.getMap()
    if (!gl) return
    const bands = bandedImageBands(!!isZoomScalingEnabled)
    logoUrls.forEach((_url, imageId) =>
      bands.forEach(band =>
        registerMissingCompanyLogoImage(
          gl,
          imageId + imageBandSuffix(band),
          anyPlated,
        ),
      ),
    )
    // icon-image is LAYOUT: mapbox does not re-lay-out a tile because an image appeared, so
    // tiles built before this pass registered the artwork have no icon quad and need rebuilding
    // — re-setting the source data is what does that. Once per logo SET, since this effect also
    // re-runs for unrelated reasons.
    if (reLaidOutForRef.current === logoUrls) return
    reLaidOutForRef.current = logoUrls
    const source = gl.getSource(LABELS_SOURCE_ID)
    if (source && 'setData' in source) {
      ;(source as { setData: (d: GeoJSON.FeatureCollection) => void }).setData(
        data,
      )
    }
  }, [map, logoUrls, hasLabels, isZoomScalingEnabled, anyPlated, data])

  // react-map-gl diffs layout and paint behind an IDENTITY check, so a fresh object per render
  // walks every key and compares whole expressions — and the host re-renders on every camera
  // frame. The FINAL per-layer objects are memoised, not just the shared base: spreading the
  // base would mint a new identity and defeat the check.
  const {
    anchoredLayout,
    freeStandingLayout,
    selectedAnchoredLayout,
    selectedFreeStandingLayout,
    sharedPaint,
    restingPaint,
  } = React.useMemo(() => {
    const sharedLayout = {
      visibility: isLabelHidden ? 'none' : 'visible',
      'text-field': ['get', 'label'],
      ...labelTypographyLayout(PIN_LABEL, { isZoomScalingEnabled }),
      'text-max-width': LABEL_MAX_WIDTH_EM,
      // Center-justified by design, including alongside a chip.
      'text-justify': 'center',
      // The chip is a stretchable image fitted around the text, so GL keeps it
      // vertically centered on the whole block without anything measuring the label.
      // Plated labels carry the plate and their chip as one image, because a symbol has
      // exactly one icon-image and the spec puts the chip inside the plate.
      // Every variant carries the band its art was baked for, so the chip and the plate's
      // corners shrink with the type instead of keeping full size inside a smaller plate.
      'icon-image': iconImage as never,
      'icon-text-fit': 'both',
      // A one-line label is shorter than the chip, which would drive the stretch bands
      // negative and drop the chip low. Padding the fit box out to the chip's height
      // keeps the bands at zero; equal bands then keep the chip centered on the whole
      // block, however many lines it wraps to.
      'icon-text-fit-padding': rampedOneLevelAhead(
        isZoomScalingEnabled,
        fitPaddingAt,
      ) as never,
      // The fitted quad, scaled from the band its art was baked for to the live zoom.
      'icon-size': fittedImageSize(isZoomScalingEnabled) as never,
      // Both layers inherit icon-allow-overlap from here, and the dynamic one needs it for a
      // second reason: mapbox only collision-tests a fitted icon box against a candidate
      // anchor when the icon does NOT allow overlap, and this box is padded far taller than
      // it paints. Turn that flag off and every candidate would be rejected by empty air.
      ...chipCarryingLabelDeclutterLayout(allowOverlap, {
        isZoomScalingEnabled,
      }),
    }

    const anchoredLayout = {
      ...sharedLayout,
      // Radial rather than the x/y pair the other layer uses, because that is the form
      // that reads as one rule here: "stand this far off the point, on this side". Both
      // are data-driven and mapbox prefers the radial whenever it evaluates non-zero, so
      // the two layers can state their offset in whichever shape fits — the same pairing
      // MarkupLayer uses for its own lift.
      'text-anchor': ANCHORED_TEXT_ANCHOR,
      'text-radial-offset': ['get', 'radialEm'] as never,
    }
    const freeStandingLayout = {
      ...sharedLayout,
      'text-anchor': 'top',
      'text-offset': textOffsetAt() as never,
    }
    const sharedPaint = {
      'text-color': pinLabelInkPaint(),
      'text-halo-color': labelHaloPaint(),
      // The plate IS the casing on a plated label, so the halo goes to zero under one — and
      // to a transparent colour with it, via labelHaloPaint above. Both halves of that rule
      // are stated in the styling module so they cannot drift apart.
      ...labelCasingPaint(PIN_LABEL, {
        isZoomScalingEnabled,
        suppressed: ['get', 'plated'],
      }),
      // Both keys: the label draws its text and, alongside a company logo, a chip image.
      ...ICON_TEXT_OCCLUSION_PAINT,
    }

    return {
      anchoredLayout,
      freeStandingLayout,
      selectedAnchoredLayout: {
        ...anchoredLayout,
        ...SELECTED_LABEL_PLACEMENT,
      },
      selectedFreeStandingLayout: {
        ...freeStandingLayout,
        ...SELECTED_LABEL_PLACEMENT,
      },
      sharedPaint,
      restingPaint: { ...sharedPaint, ...RESTING_HIDDEN },
    }
  }, [iconImage, isZoomScalingEnabled, allowOverlap, isLabelHidden])

  return (
    <Source
      id={LABELS_SOURCE_ID}
      type="geojson"
      data={data}
      // Feature state binds by feature id, and these features have none of their own — without
      // this, every setFeatureState below would be filed against a feature that does not exist.
      // coordKey is 1:1 with a label: labels are only built for coord groups of one.
      promoteId="coordKey"
    >
      <Layer
        id={WORKFLOW_PIN_LABELS_ANCHORED_LAYER_ID}
        type="symbol"
        minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        maxzoom={MAX_ITEM_ZOOM_LEVEL}
        filter={PIN_LABEL_FILTERS.anchored as never}
        layout={anchoredLayout as never}
        paint={restingPaint as never}
      />
      <Layer
        id={WORKFLOW_PIN_LABELS_LAYER_ID}
        type="symbol"
        minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        maxzoom={MAX_ITEM_ZOOM_LEVEL}
        filter={PIN_LABEL_FILTERS.freeStanding as never}
        layout={freeStandingLayout as never}
        paint={restingPaint as never}
      />
      <Layer
        id={WORKFLOW_PIN_LABELS_SELECTED_ANCHORED_LAYER}
        type="symbol"
        minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        maxzoom={MAX_ITEM_ZOOM_LEVEL}
        filter={PIN_LABEL_FILTERS.selectedAnchored as never}
        layout={selectedAnchoredLayout as never}
        paint={sharedPaint as never}
      />
      <Layer
        id={WORKFLOW_PIN_LABELS_SELECTED_LAYER}
        type="symbol"
        minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        maxzoom={MAX_ITEM_ZOOM_LEVEL}
        filter={PIN_LABEL_FILTERS.selectedFreeStanding as never}
        layout={selectedFreeStandingLayout as never}
        paint={sharedPaint as never}
      />
    </Source>
  )
}

// Memoized: the host re-renders every camera frame; `points` is a stable @computed.
export default React.memo(GLWorkflowPinLabelLayer)
